import { addAuthTokenToCookieForLocalDevelopment } from "../common/utils/util";

declare global {
    interface Window {
        Mhecomm_Env: string;
        Mhecomm_Config: Environment;
        Mhecomm_Theme: string;
        environment: Environment;
    }
}

export enum CheckoutFlow {
    national = "us",
    hestores = "hestores",
    k12stores = "k12stores",
    tunnelPurchase = "tunnel-purchase"
}

const K12_STORE_LIVE_PAGES_PATTERN: RegExp[] = [
    new RegExp(/\/prek-12\/custom\/store/),
    new RegExp(/\/checkout\/([a-zA-Z\-\.0-9]+)\/cart/),
    new RegExp(/\/checkout\/([a-zA-Z\-\.0-9]+)\/payment/),
    new RegExp(/\/checkout\/([a-zA-Z\-\.0-9]+)\/success/)
];

const GUEST_CHECKOUT_LIVE_PAGES_PATTERN: RegExp[] = [
    new RegExp(/payment\/guest(.html)?$/),
    new RegExp(/success\/guest(.html)?$/)
]

const GUEST_CHECKOUT_AUTHOR_PAGES_PATTERN: RegExp[] = [
    new RegExp(/checkout\/guest.*/)
]

const HE_STORE_LIVE_PAGES_PATTERN: string[] = [
    "/checkout/hestore-",
    "/checkout/highered-store",
    "/highered/bookstore",
    "/highered/custom/product",
    "/highered/custom/store",
];

const K12_STORE_LOCAL_PAGES_PATTERN: string[] = [
    "storefront",
    "k12storefront",
    "prek12-store-front"
];

const HE_STORE_LOCAL_PAGES_PATTERN: string[] = [
  '/content/mhe/en_us/bookstore',
  '/content/mhe/en_us/custom',
  '/content/mheducation/us/en/checkout/hestore-',
  '/content/mheducation/us/en/highered/custom',
  '/content/mheducation/us/en/highered/custom/product/',
];

declare interface Environment {
    env: ENV;
    searchServiceUrl: string;
    shopUrl: string;
    searchUrl: string;
    selfUrl: string;
    mheducationUrl: string;
    magentoUrl: string;
    themeUrl: string;
    myBookShelfUrl: string;
    accountUrl: string;
    loginUrl: string;
    baseLoginUrl: string;
    cartUrl: string;
    customStoreCartUrl: string;
    customerServiceUrl: string;
    checkoutFlow: CheckoutFlow;
    connectUrl: string;
    firstAidForwadAppUrl: string;
    clinicalReasoningAppUrl: string;
    inclusiveAccessUrl: string;
    optInOutUrl: string;
    searchV2Api: string;
    supportSearchUrl: string;
    cancelFreeTrialHelpUrl: string;
    consumerPurchaseTermsUrl: string;
    privacyNoticeUrl: string;
    termsOfUseUrl: string;
    sharpenHomePage: string;
    returnPolicyUrl: string;
    sharpenUsMailto: string;
    boardsBeyondUrl: string;
    videoViewingNoticeUrl: string;
    representativeListUrl: string;
    representativeSchoolListUrl: string;
    createUrl: string;
    enableEventLogger: boolean;
    rmsUrl: string;
}

const WWW_DASH = location.hostname.includes(`www-`) ? `www-` : ``;

export const enum ENV {
    local = 'local',
    dev = 'dev',
    qastg = 'qastg',
    qalv = 'qalv',
    qalv2 = 'qalv2',
    pqa = 'pqa',
    demo = 'demo',
    prod = 'prod',
}

const subdomainInactiveCloudfrontUrls = [
    "www-test-dev.eops-adm.nonprod.mheducation.com",
    "www-test-qastg.eops-adm.nonprod.mheducation.com",
    "www-test-qalv.eops-adm.nonprod.mheducation.com",
    "www-test-demo.eops-adm.nonprod.mheducation.com",
    "www-test-prod.ecom-ady.prod.mheducation.com"
]
const isInactiveUrl = subdomainInactiveCloudfrontUrls.includes(location.host)

const checkoutFlow = determineCheckoutFlow(location.pathname);

// Note: You need to be running the dev-proxy for local development.
export const local: Environment = {
    env: ENV.local,
    searchServiceUrl: `${window.location.origin}/searchservice`,
    shopUrl: window.location.origin,
    searchUrl: `https://${WWW_DASH}dev.mheducation.com/search.html`,
    selfUrl: `${window.location.origin}/accounts/api/v1`,
    mheducationUrl: `https://${WWW_DASH}dev.mheducation.com`,
    magentoUrl: `${window.location.origin}/shop`,
    checkoutFlow: checkoutFlow,
    themeUrl: window.Mhecomm_Theme || `/uxt/themes/latest/mhcorp.css`,
    myBookShelfUrl: 'https://myebooks-dev.mheducation.com',
    loginUrl: `https://shopdev.mheducation.com/login?redirectUrl=https:%2F%2Fdev.mheducation.com%2F`,
    baseLoginUrl: `https://shopdev.mheducation.com/login`,
    cartUrl: `https://dev.mheducation.com/checkout/cart`,
    customStoreCartUrl: `https://dev.mheducation.com/checkout/:storeCode/cart`,
    accountUrl: `https://dev.mheducation.com/account`,
    customerServiceUrl: `https://mh.my.site.com/CSOM/s`,
    connectUrl:"https://connectdev.mheducation.com",
    firstAidForwadAppUrl: "https://firstaid-dev.mheducation.com",
    clinicalReasoningAppUrl: "https://dev.clinicalreasoning.nonprod.mheducation.com",
    inclusiveAccessUrl: `${window.location.origin}/accounts/api/v1/user/optinout/details`,
    optInOutUrl: `${window.location.origin}/accounts/api/v1/optinout`,
    searchV2Api: `${window.location.origin}/ecomm-search`,
    supportSearchUrl: `${window.location.origin}/content/mheducation/us/en/support/search.html?wcmmode=disabled`,
    cancelFreeTrialHelpUrl: `${window.location.origin}/content/mheducation/us/en/about-us/policy-center/terms-use/how-to-cancel-free-trial.html?wcmmode=disabled`,
    consumerPurchaseTermsUrl: `${window.location.origin}/content/mheducation/us/en/about-us/policy-center/terms-use.html#consumer-purchase-terms?wcmmode=disabled`,
    privacyNoticeUrl: `${window.location.origin}/content/mheducation/us/en/about-us/trust-center/privacy.html?wcmmode=disabled`,
    termsOfUseUrl: `${window.location.origin}/content/mheducation/us/en/about-us/policy-center/terms-use.html?wcmmode=disabled`,
    sharpenHomePage: 'https://studysharpen.com',
    returnPolicyUrl: 'https://dev.mheducation.com/return-policy.html',
    sharpenUsMailto: 'mailto:hello@studysharpen.com',
    boardsBeyondUrl: 'https://dev.boardsbeyond.com',
    videoViewingNoticeUrl: `${window.location.origin}/content/mheducation/us/en/about-us/policy-center/video-viewing-notice.html?wcmmode=disabled`,
    representativeListUrl: `${window.location.origin}/content/highered-new/en_us/support/find-your-rep/ltr-list.html?wcmmode=disabled`,
    representativeSchoolListUrl: `${window.location.origin}/content/highered-new/en_us/support/find-your-rep/school-list.html?wcmmode=disabled`,
    createUrl: 'https://createdev.mheducation.com',
    enableEventLogger: false,
    rmsUrl: 'https://rmsdev.mheducation.com/rms-rest'
}

export const dev: Environment = {
    env: ENV.dev,
    searchServiceUrl: isInactiveUrl ? `${window.location.origin}/searchservice` : `https://${WWW_DASH}dev.mheducation.com/searchservice`,
    shopUrl: `https://shopdev.mheducation.com`,
    searchUrl: isInactiveUrl ? `${window.location.origin}/search.html` : `https://${WWW_DASH}dev.mheducation.com/search.html`,
    selfUrl: `https://accounts-api-dev.mheducation.com/api/v1`,
    mheducationUrl: isInactiveUrl ? `${window.location.origin}` : `https://${WWW_DASH}dev.mheducation.com`,
    magentoUrl: isInactiveUrl ? `${window.location.origin}/shop` : `https://${WWW_DASH}dev.mheducation.com/shop`,
    checkoutFlow: checkoutFlow,
    themeUrl: window.Mhecomm_Theme || `/uxt/themes/latest/mhcorp.css`,
    myBookShelfUrl: 'https://myebooks-dev.mheducation.com',
    loginUrl: `https://shopdev.mheducation.com/login?redirectUrl=https:%2F%2Fdev.mheducation.com%2F`,
    baseLoginUrl: `https://shopdev.mheducation.com/login`,
    cartUrl: isInactiveUrl ? `${window.location.origin}/checkout/cart`: `https://${WWW_DASH}dev.mheducation.com/checkout/cart`,
    customStoreCartUrl: isInactiveUrl ? `${window.location.origin}/checkout/:storeCode/cart`: `https://${WWW_DASH}dev.mheducation.com/checkout/:storeCode/cart`,
    accountUrl: `https://dev.mheducation.com/account`,
    customerServiceUrl: `https://mh.my.site.com/CSOM/s`,
    connectUrl:"https://connectdev.mheducation.com",
    firstAidForwadAppUrl: "https://firstaid-dev.mheducation.com",
    clinicalReasoningAppUrl: "https://dev.clinicalreasoning.nonprod.mheducation.com",
    inclusiveAccessUrl: `https://accounts-api-dev.mheducation.com/api/v1/user/optinout/details`,
    optInOutUrl: `https://accounts-api-dev.mheducation.com/api/v1/optinout`,
    searchV2Api: `https://dev.mheducation.com/ecomm-search`,
    supportSearchUrl: `https://dev.mheducation.com/support/search.html`,
    cancelFreeTrialHelpUrl: `https://dev.mheducation.com/terms-use/how-to-cancel-free-trial.html`,
    consumerPurchaseTermsUrl: `https://dev.mheducation.com/terms-use.html#consumer-purchase-terms`,
    privacyNoticeUrl: `https://dev.mheducation.com/privacy.html`,
    termsOfUseUrl: `https://dev.mheducation.com/terms-use.html`,
    sharpenHomePage: 'https://studysharpen.com',
    returnPolicyUrl: 'https://dev.mheducation.com/return-policy.html',
    sharpenUsMailto: 'mailto:hello@studysharpen.com',
    boardsBeyondUrl: 'https://dev.boardsbeyond.com',
    videoViewingNoticeUrl: 'https://dev.mheducation.com/privacy/video-viewing-notice.html',
    representativeListUrl: 'https://dev.mheducation.com/highered/support/find-your-rep/ltr-list.html',
    representativeSchoolListUrl: 'https://dev.mheducation.com/highered/support/find-your-rep/school-list.html',
    createUrl: 'https://createdev.mheducation.com',
    enableEventLogger: false,
    rmsUrl: 'https://rmsdev.mheducation.com/rms-rest'
}
export const qastg: Environment = {
    env: ENV.qastg,
    searchServiceUrl: isInactiveUrl ? `${window.location.origin}/searchservice` : `https://${WWW_DASH}qastg.mheducation.com/searchservice`,
    shopUrl: `https://shopqastg.mheducation.com`,
    searchUrl: isInactiveUrl ? `${window.location.origin}/search.html` : `https://${WWW_DASH}qastg.mheducation.com/search.html`,
    selfUrl: `https://accounts-api-qastg.mheducation.com/api/v1`,
    mheducationUrl: isInactiveUrl ? `${window.location.origin}` : `https://${WWW_DASH}qastg.mheducation.com`,
    magentoUrl: isInactiveUrl ? `${window.location.origin}/shop` : `https://${WWW_DASH}qastg.mheducation.com/shop`,
    checkoutFlow: checkoutFlow,
    themeUrl: window.Mhecomm_Theme || `/uxt/themes/latest/mhcorp.css`,
    myBookShelfUrl: 'https://myebooks-qastg.mheducation.com',
    loginUrl: `https://shopqastg.mheducation.com/login?redirectUrl=https:%2F%2Fqastg.mheducation.com%2F`,
    baseLoginUrl: `https://shopqastg.mheducation.com/login`,
    cartUrl: isInactiveUrl ? `${window.location.origin}/checkout/cart`: `https://${WWW_DASH}qastg.mheducation.com/checkout/cart`,
    customStoreCartUrl: isInactiveUrl ? `${window.location.origin}/checkout/:storeCode/cart`: `https://${WWW_DASH}qastg.mheducation.com/checkout/:storeCode/cart`,
    accountUrl: `https://qastg.mheducation.com/account`,
    customerServiceUrl: `https://mh.my.site.com/CSOM/s`,
    connectUrl:"https://connectqastg.mheducation.com",
    firstAidForwadAppUrl: "https://firstaid-qastg.mheducation.com",
    clinicalReasoningAppUrl: "https://qastg.clinicalreasoning.nonprod.mheducation.com",
    inclusiveAccessUrl: `https://accounts-api-qastg.mheducation.com/api/v1/user/optinout/details`,
    optInOutUrl: `https://accounts-api-qastg.mheducation.com/api/v1/optinout`,
    searchV2Api: `https://qastg.mheducation.com/ecomm-search`,
    supportSearchUrl: `https://qastg.mheducation.com/support/search.html`,
    cancelFreeTrialHelpUrl: `https://qastg.mheducation.com/terms-use/how-to-cancel-free-trial.html`,
    consumerPurchaseTermsUrl: `https://qastg.mheducation.com/terms-use.html#consumer-purchase-terms`,
    privacyNoticeUrl: `https://qastg.mheducation.com/privacy.html`,
    termsOfUseUrl: `https://qastg.mheducation.com/terms-use.html`,
    sharpenHomePage: 'https://studysharpen.com',
    returnPolicyUrl: 'https://qastg.mheducation.com/return-policy.html',
    sharpenUsMailto: 'mailto:hello@studysharpen.com',
    boardsBeyondUrl: 'https://staging.boardsbeyond.com',
    videoViewingNoticeUrl: 'https://qastg.mheducation.com/privacy/video-viewing-notice.html',
    representativeListUrl: 'https://qastg.mheducation.com/highered/support/find-your-rep/ltr-list.html',
    representativeSchoolListUrl: 'https://qastg.mheducation.com/highered/support/find-your-rep/school-list.html',
    createUrl: 'https://createqa.mheducation.com',
    enableEventLogger: false,
    rmsUrl: 'https://rmsqastg.mheducation.com/rms-rest'
}

export const qalv: Environment = {
    env: ENV.qalv,
    searchServiceUrl: isInactiveUrl ? `${window.location.origin}/searchservice` : `https://${WWW_DASH}qalv.mheducation.com/searchservice`,
    shopUrl: `https://shopqalv.mheducation.com`,
    searchUrl: isInactiveUrl ? `${window.location.origin}/search.html` : `https://${WWW_DASH}qalv.mheducation.com/search.html`,
    selfUrl: `https://accounts-api-qalv.mheducation.com/api/v1`,
    mheducationUrl: isInactiveUrl ? `${window.location.origin}` : `https://${WWW_DASH}qalv.mheducation.com`,
    magentoUrl: isInactiveUrl ? `${window.location.origin}/shop` : `https://${WWW_DASH}qalv.mheducation.com/shop`,
    checkoutFlow: checkoutFlow,
    themeUrl: window.Mhecomm_Theme || `/uxt/themes/latest/mhcorp.css`,
    myBookShelfUrl: 'https://myebooks-qalv.mheducation.com',
    loginUrl: `https://shopqalv.mheducation.com/login?redirectUrl=https:%2F%2Fqalv.mheducation.com%2F`,
    baseLoginUrl: `https://shopqalv.mheducation.com/login`,
    cartUrl: isInactiveUrl ? `${window.location.origin}/checkout/cart`: `https://${WWW_DASH}qalv.mheducation.com/checkout/cart`,
    customStoreCartUrl: isInactiveUrl ? `${window.location.origin}/checkout/:storeCode/cart`: `https://${WWW_DASH}qalv.mheducation.com/checkout/:storeCode/cart`,
    accountUrl: `https://qalv.mheducation.com/account`,
    customerServiceUrl: `https://mh.my.site.com/CSOM/s`,
    connectUrl:"https://connectqalv.mheducation.com",
    firstAidForwadAppUrl: "https://firstaid-qalv.mheducation.com",
    clinicalReasoningAppUrl: "https://qalv.clinicalreasoning.nonprod.mheducation.com",
    inclusiveAccessUrl: `https://accounts-api-qalv.mheducation.com/api/v1/user/optinout/details`,
    optInOutUrl: `https://accounts-api-qalv.mheducation.com/api/v1/optinout`,
    searchV2Api: `https://qalv.mheducation.com/ecomm-search`,
    supportSearchUrl: `https://qalv.mheducation.com/support/search.html`,
    cancelFreeTrialHelpUrl: `https://qalv.mheducation.com/terms-use/how-to-cancel-free-trial.html`,
    consumerPurchaseTermsUrl: `https://qalv.mheducation.com/terms-use.html#consumer-purchase-terms`,
    privacyNoticeUrl: `https://qalv.mheducation.com/privacy.html`,
    termsOfUseUrl: `https://qalv.mheducation.com/terms-use.html`,
    sharpenHomePage: 'https://studysharpen.com',
    returnPolicyUrl: 'https://qalv.mheducation.com/return-policy.html',
    sharpenUsMailto: 'mailto:hello@studysharpen.com',
    boardsBeyondUrl: 'https://staging.boardsbeyond.com',
    videoViewingNoticeUrl: 'https://qalv.mheducation.com/privacy/video-viewing-notice.html',
    representativeListUrl: 'https://qalv.mheducation.com/highered/support/find-your-rep/ltr-list.html',
    representativeSchoolListUrl: 'https://qalv.mheducation.com/highered/support/find-your-rep/school-list.html',
    createUrl: 'https://createqa.mheducation.com',
    enableEventLogger: false,
    rmsUrl: 'https://rmsqalv.mheducation.com/rms-rest'
}

export const qalv2: Environment = {
    env: ENV.qalv2,
    searchServiceUrl: isInactiveUrl ? `${window.location.origin}/searchservice` : `https://${WWW_DASH}qalv.mheducation.com/searchservice`,
    shopUrl: `https://shopqalv.mheducation.com`,
    searchUrl: isInactiveUrl ? `${window.location.origin}/search.html` : `https://${WWW_DASH}qalv2.mheducation.com/search.html`,
    selfUrl: `https://accounts-api-qalv.mheducation.com/api/v1`,
    mheducationUrl: isInactiveUrl ? `${window.location.origin}` : `https://${WWW_DASH}qalv2.mheducation.com`,
    magentoUrl: isInactiveUrl ? `${window.location.origin}/shop` : `https://${WWW_DASH}qalv.mheducation.com/shop`,
    checkoutFlow: checkoutFlow,
    themeUrl: window.Mhecomm_Theme || `/uxt/themes/latest/mhcorp.css`,
    myBookShelfUrl: 'https://myebooks-qalv.mheducation.com',
    loginUrl: `https://shopqalv.mheducation.com/login?redirectUrl=https:%2F%2Fqalv.mheducation.com%2F`,
    baseLoginUrl: `https://shopqalv.mheducation.com/login`,
    cartUrl: isInactiveUrl ? `${window.location.origin}/checkout/cart`: `https://${WWW_DASH}qalv2.mheducation.com/checkout/cart`,
    customStoreCartUrl: isInactiveUrl ? `${window.location.origin}/checkout/:storeCode/cart`: `https://${WWW_DASH}qalv2.mheducation.com/checkout/:storeCode/cart`,
    accountUrl: `https://qalv2.mheducation.com/account`,
    customerServiceUrl: `https://mh.my.site.com/CSOM/s`,
    connectUrl:"https://connectqalv.mheducation.com",
    firstAidForwadAppUrl: "https://firstaid-qalv.mheducation.com",
    clinicalReasoningAppUrl: "https://qalv.clinicalreasoning.nonprod.mheducation.com",
    inclusiveAccessUrl: `https://accounts-api-qalv.mheducation.com/api/v1/user/optinout/details`,
    optInOutUrl: `https://accounts-api-qalv.mheducation.com/api/v1/optinout`,
    searchV2Api: `https://qalv.mheducation.com/ecomm-search`,
    supportSearchUrl: `https://qalv2.mheducation.com/support/search.html`,
    cancelFreeTrialHelpUrl: `https://qalv2.mheducation.com/terms-use/how-to-cancel-free-trial.html`,
    consumerPurchaseTermsUrl: `https://qalv2.mheducation.com/terms-use.html#consumer-purchase-terms`,
    privacyNoticeUrl: `https://qalv2.mheducation.com/privacy.html`,
    termsOfUseUrl: `https://qalv2.mheducation.com/terms-use.html`,
    sharpenHomePage: 'https://studysharpen.com',
    returnPolicyUrl: 'https://qalv2.mheducation.com/return-policy.html',
    sharpenUsMailto: 'mailto:hello@studysharpen.com',
    boardsBeyondUrl: 'https://staging.boardsbeyond.com',
    videoViewingNoticeUrl: 'https://qalv2.mheducation.com/privacy/video-viewing-notice.html',
    representativeListUrl: 'https://qalv2.mheducation.com/highered/support/find-your-rep/ltr-list.html',
    representativeSchoolListUrl: 'https://qalv2.mheducation.com/highered/support/find-your-rep/school-list.html',
    createUrl: 'https://createqa.mheducation.com',
    enableEventLogger: false,
    rmsUrl: 'https://rmsqalv.mheducation.com/rms-rest'
}

export const demo: Environment = {
    env: ENV.demo,
    searchServiceUrl: isInactiveUrl ? `${window.location.origin}/searchservice` : `https://${WWW_DASH}demo.mheducation.com/searchservice`,
    shopUrl: `https://shopdemo.mheducation.com`,
    searchUrl: isInactiveUrl ? `${window.location.origin}/search.html` : `https://${WWW_DASH}demo.mheducation.com/search.html`,
    selfUrl: `https://accounts-api-demo.mheducation.com/api/v1`,
    mheducationUrl: isInactiveUrl ? `${window.location.origin}` : `https://${WWW_DASH}demo.mheducation.com`,
    magentoUrl: isInactiveUrl ? `${window.location.origin}/shop` : `https://${WWW_DASH}demo.mheducation.com/shop`,
    checkoutFlow: checkoutFlow,
    themeUrl: window.Mhecomm_Theme || `/uxt/themes/latest/mhcorp.css`,
    myBookShelfUrl: 'https://myebooks-demo.mheducation.com',
    loginUrl: `https://shopdemo.mheducation.com/login?redirectUrl=https:%2F%2Fdemo.mheducation.com%2F`,
    baseLoginUrl: `https://shopdemo.mheducation.com/login`,
    cartUrl: isInactiveUrl ? `${window.location.origin}/checkout/cart`: `https://${WWW_DASH}demo.mheducation.com/checkout/cart`,
    customStoreCartUrl: isInactiveUrl ? `${window.location.origin}/checkout/:storeCode/cart`: `https://${WWW_DASH}demo.mheducation.com/checkout/:storeCode/cart`,
    accountUrl: `https://demo.mheducation.com/account`,
    customerServiceUrl: `https://mh.my.site.com/CSOM/s`,
    connectUrl:"https://connectdemo.mheducation.com",
    firstAidForwadAppUrl: "https://firstaid-demo.mheducation.com",
    clinicalReasoningAppUrl: "https://demo.clinicalreasoning.nonprod.mheducation.com",
    inclusiveAccessUrl: `https://accounts-api-demo.mheducation.com/api/v1/user/optinout/details`,
    optInOutUrl: `https://accounts-api-demo.mheducation.com/api/v1/optinout`,
    searchV2Api: `https://demo.mheducation.com/ecomm-search`,
    supportSearchUrl: `https://demo.mheducation.com/support/search.html`,
    cancelFreeTrialHelpUrl: `https://demo.mheducation.com/terms-use/how-to-cancel-free-trial.html`,
    consumerPurchaseTermsUrl: `https://demo.mheducation.com/terms-use.html#consumer-purchase-terms`,
    privacyNoticeUrl: `https://demo.mheducation.com/privacy.html`,
    termsOfUseUrl: `https://demo.mheducation.com/terms-use.html`,
    sharpenHomePage: 'https://studysharpen.com',
    returnPolicyUrl: 'https://demo.mheducation.com/return-policy.html',
    sharpenUsMailto: 'mailto:hello@studysharpen.com',
    boardsBeyondUrl: 'https://staging.boardsbeyond.com',
    videoViewingNoticeUrl: 'https://demo.mheducation.com/privacy/video-viewing-notice.html',
    representativeListUrl: 'https://demo.mheducation.com/highered/support/find-your-rep/ltr-list.html',
    representativeSchoolListUrl: 'https://demo.mheducation.com/highered/support/find-your-rep/school-list.html',
    createUrl: 'https://createqa.mheducation.com',
    enableEventLogger: false,
    rmsUrl: 'https://rmsdemo.mheducation.com/rms-rest'
}

export const prod: Environment = {
    env: ENV.prod,
    searchServiceUrl: isInactiveUrl ? `${window.location.origin}/searchservice` : `https://www.mheducation.com/searchservice`,
    shopUrl: `https://shop.mheducation.com`,
    searchUrl: isInactiveUrl ? `${window.location.origin}/search.html` : `https://www.mheducation.com/search.html`,
    selfUrl: `https://accounts-api.mheducation.com/api/v1`,
    mheducationUrl: isInactiveUrl ? `${window.location.origin}` : `https://www.mheducation.com`,
    magentoUrl: isInactiveUrl ? `${window.location.origin}/shop` : `https://www.mheducation.com/shop`,
    checkoutFlow: checkoutFlow,
    themeUrl: window.Mhecomm_Theme || `/uxt/themes/latest/mhcorp.css`,
    myBookShelfUrl: 'https://myebooks.mheducation.com',
    loginUrl: `https://shop.mheducation.com/login?redirectUrl=https:%2F%2Fwww.mheducation.com%2F`,
    baseLoginUrl: `https://shop.mheducation.com/login`,
    cartUrl: isInactiveUrl ? `${window.location.origin}/checkout/cart`: `https://www.mheducation.com/checkout/cart`,
    customStoreCartUrl: isInactiveUrl ? `${window.location.origin}/checkout/:storeCode/cart`: `https://www.mheducation.com/checkout/:storeCode/cart`,
    accountUrl: `https://mheducation.com/account`,
    customerServiceUrl: `https://mh.my.site.com/CSOM/s`,
    connectUrl:"https://connect.mheducation.com",
    firstAidForwadAppUrl: "https://firstaid.mheducation.com",
    clinicalReasoningAppUrl: "https://clinicalreasoning.mheducation.com",
    inclusiveAccessUrl: `https://accounts-api.mheducation.com/api/v1/user/optinout/details`,
    optInOutUrl: `https://accounts-api.mheducation.com/api/v1/optinout`,
    searchV2Api: `https://www.mheducation.com/ecomm-search`,
    supportSearchUrl: `https://www.mheducation.com/support/search.html`,
    cancelFreeTrialHelpUrl: `https://www.mheducation.com/terms-use/how-to-cancel-free-trial.html`,
    consumerPurchaseTermsUrl: `https://www.mheducation.com/terms-use.html#consumer-purchase-terms`,
    privacyNoticeUrl: `https://www.mheducation.com/privacy.html`,
    termsOfUseUrl: `https://www.mheducation.com/terms-use.html`,
    sharpenHomePage: 'https://studysharpen.com',
    returnPolicyUrl: 'https://www.mheducation.com/return-policy.html',
    sharpenUsMailto: 'mailto:hello@studysharpen.com',
    boardsBeyondUrl: 'https://boardsbeyond.com',
    videoViewingNoticeUrl: 'https://www.mheducation.com/privacy/video-viewing-notice.html',
    representativeListUrl: 'https://www.mheducation.com/highered/support/find-your-rep/ltr-list.html',
    representativeSchoolListUrl: 'https://www.mheducation.com/highered/support/find-your-rep/school-list.html',
    createUrl: 'https://create.mheducation.com',
    enableEventLogger: false,
    rmsUrl: 'https://rms.mheducation.com/rms-rest',
}

export const envMap: { [key: string]: Environment } = { local, dev, qastg, qalv, prod };

function environmentComparisonSource() {
    const metaEnv = document.querySelector('meta[name="mhe-env-type"]')?.content?.trim();
    return metaEnv ? metaEnv + "." : location.hostname;
}

export function isLocalEnv() {
    return environmentComparisonSource().includes(`local`);
}

export function isProdEnv() { 
    return environment().env === ENV.prod;
}
export default function environment(): Environment {
    let env: Environment;
    if (window.Mhecomm_Config !== undefined) {
        env = window.Mhecomm_Config;
    } else if (window.Mhecomm_Env !== undefined) {
        env = envMap[window.Mhecomm_Env];
    } else if (isLocalEnv()) {
        addAuthTokenToCookieForLocalDevelopment();
        env = local;
    } else {
        let comparisonSource = environmentComparisonSource();
        if (comparisonSource.includes(`dev.`)) {
            env = dev;
        } else if (comparisonSource.includes(`qastg.`)) {
            env = qastg;
        } else if (comparisonSource.includes(`qalv2.`)) {
            env = qalv2;
        } else if (comparisonSource.includes(`qalv.`)) {
            env = qalv;
        } else if (comparisonSource.includes(`demo.`)) {
            env = demo;
        }  else {
            env = prod;
        }
    }

    window.environment = env;
    return env;
}

export function determineCheckoutFlow(pathname: string) {
    if (checkIfHeStorePage(pathname)) {
        return CheckoutFlow.hestores;
    } else if (checkIfK12StorePage(pathname)) {
        return CheckoutFlow.k12stores;
    } else if (pathname.includes(`tunnel-`)) {
        return CheckoutFlow.tunnelPurchase;
    } else {
        return CheckoutFlow.national;
    }
}

/*
    The optional parameter is to help on the unit testing, as
    I haven't yet discovered how to mock the location on the unit tests.
*/
export function isGuestCheckoutPage(pathname?: string) {
    if (!pathname) {
        pathname = location.pathname;
    }

    if (isLocalEnv() && checkPathnameMatchesPattern(pathname, GUEST_CHECKOUT_AUTHOR_PAGES_PATTERN)) {
        return true;
    }

    return checkPathnameMatchesPattern(pathname, GUEST_CHECKOUT_LIVE_PAGES_PATTERN);
}

function checkIfHeStorePage(pathname: string) {
    if (isLocalEnv() && checkPathnameMatchesPattern(pathname, HE_STORE_LOCAL_PAGES_PATTERN)) {
        return true;
    }

    return checkPathnameMatchesPattern(pathname, HE_STORE_LIVE_PAGES_PATTERN);
}

function checkIfK12StorePage(pathname: string) {
    if (isLocalEnv() && checkPathnameMatchesPattern(pathname, K12_STORE_LOCAL_PAGES_PATTERN)) {
        return true;
    }

    return checkPathnameMatchesPattern(pathname, K12_STORE_LIVE_PAGES_PATTERN);
}

function checkPathnameMatchesPattern(pathname: string, patterns: RegExp[] | string[]) {
    if ( patterns[0] instanceof RegExp) {
        return patterns.some(pattern => pattern.test(pathname));
    }

    return patterns.some(pattern => pathname.includes(pattern));
}

export function isSuccessPage(location: string) {
    return /success/.test(location);
}

